/**
 * AudioCaptureService - Captura audio del micrófono local y del audio remoto
 * de Telnyx WebRTC para streaming de transcripción en tiempo real.
 *
 * Arquitectura:
 * 1. getUserMedia() → captura micrófono del agente
 * 2. HTMLAudioElement.captureStream() → captura audio remoto del widget
 * 3. AudioContext + AudioWorkletNode → downsample a 16kHz PCM16
 * 4. Emite chunks binarios con indicador de canal (agente/cliente)
 */

import { logger } from '@/utils/logger';
import { AUDIO_CHANNEL_AGENT, AUDIO_CHANNEL_CUSTOMER } from '@/types/transcription';
import type { TranscriptionChannel } from '@/types/transcription';

export type AudioChunkCallback = (
  chunk: ArrayBuffer,
  channel: TranscriptionChannel,
) => void;

export class AudioCaptureService {
  private audioContext: AudioContext | null = null;
  private micStream: MediaStream | null = null;
  private agentWorklet: AudioWorkletNode | null = null;
  private customerWorklet: AudioWorkletNode | null = null;
  private chunkCallback: AudioChunkCallback | null = null;
  private isCapturing = false;
  /** GainNode silencioso conectado al destination para forzar el pull del audio graph */
  private silentSink: GainNode | null = null;
  /** Stream clonado del remoto (para cleanup) */
  private clonedRemoteStream: MediaStream | null = null;

  /**
   * Verifica si el navegador soporta AudioWorklet y getUserMedia.
   */
  static isSupported(): boolean {
    return (
      typeof AudioContext !== 'undefined' &&
      typeof AudioWorkletNode !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia
    );
  }

  /**
   * AudioContext pre-calentado con worklet ya cargado.
   * Creado por preloadWorklet() y consumido (reclamado) por start().
   * Evita la latencia de ~1-2s de addModule() al iniciar cada llamada.
   */
  private static prewarmedContext: AudioContext | null = null;
  private static workletPreloadPromise: Promise<void> | null = null;

  /**
   * Precarga el worklet en segundo plano para que la primera llamada a start() sea instantánea.
   * Mantiene el AudioContext suspendido (NO cerrado) para que el worklet quede cargado en memoria.
   * Idempotente: se puede llamar varias veces.
   */
  static preloadWorklet(): Promise<void> {
    if (!AudioCaptureService.isSupported()) return Promise.resolve();
    if (AudioCaptureService.workletPreloadPromise)
      return AudioCaptureService.workletPreloadPromise;
    AudioCaptureService.workletPreloadPromise = (async () => {
      const ctx = new AudioContext({ sampleRate: 48000 });
      const workletUrl = new URL('/audio-processor.worklet.js', window.location.origin);
      await ctx.audioWorklet.addModule(workletUrl.href);
      // Suspender en lugar de cerrar → worklet queda cargado, listo para reanudar
      await ctx.suspend();
      AudioCaptureService.prewarmedContext = ctx;
      console.warn('[AudioCapture] ✅ Worklet pre-calentado y context suspendido (listo para next call)');
    })();
    return AudioCaptureService.workletPreloadPromise;
  }

  /**
   * Inicia la captura de audio del micrófono y opcionalmente del audio remoto.
   *
   * @param remoteAudioElement - Elemento <audio> remoto (puede ser null si se usa MediaStream directo)
   */
  async start(remoteAudioElement: HTMLAudioElement | null): Promise<void> {
    if (this.isCapturing) {
      logger.warn('[AudioCapture] Ya está capturando');
      return;
    }

    if (!AudioCaptureService.isSupported()) {
      throw new Error('AudioWorklet o getUserMedia no soportado en este navegador');
    }

    try {
      console.warn('[AudioCapture] 🎙️ Iniciando captura de audio...');

      // 1. Reutilizar AudioContext pre-calentado si está disponible (worklet ya cargado → sin latencia)
      if (
        AudioCaptureService.prewarmedContext &&
        AudioCaptureService.prewarmedContext.state !== 'closed'
      ) {
        this.audioContext = AudioCaptureService.prewarmedContext;
        AudioCaptureService.prewarmedContext = null;    // Reclamar para uso exclusivo
        AudioCaptureService.workletPreloadPromise = null; // Permitir re-precarga en la próxima llamada
        console.warn('[AudioCapture] ✅ Reutilizando AudioContext pre-calentado. state:', this.audioContext.state);
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
          console.warn('[AudioCapture] AudioContext reanudado. state:', this.audioContext.state);
        }
      } else {
        // Fallback: crear AudioContext nuevo y cargar worklet (tarda ~1-2s)
        this.audioContext = new AudioContext({ sampleRate: 48000 });
        console.warn('[AudioCapture] AudioContext creado (sin pre-calentar). sampleRate:', this.audioContext.sampleRate, 'state:', this.audioContext.state);
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
          console.warn('[AudioCapture] AudioContext reanudado. state:', this.audioContext.state);
        }
        const workletUrl = new URL('/audio-processor.worklet.js', window.location.origin);
        console.warn('[AudioCapture] Cargando worklet desde:', workletUrl.href);
        await this.audioContext.audioWorklet.addModule(workletUrl.href);
        console.warn('[AudioCapture] ✅ Worklet cargado');
      }

      // 2.5. Crear GainNode silencioso → destination
      // Esto fuerza al audio graph a "tirar" (pull) de los nodos conectados,
      // garantizando que process() se ejecute en los AudioWorkletNodes.
      // Sin esto, Chrome puede no invocar process() en worklets no conectados al destination.
      this.silentSink = this.audioContext.createGain();
      this.silentSink.gain.value = 0; // Silencio total
      this.silentSink.connect(this.audioContext.destination);

      // 3. Capturar micrófono del agente
      this.micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: { ideal: 48000 },
        },
      });
      console.warn('[AudioCapture] ✅ Micrófono capturado. Tracks:', this.micStream.getAudioTracks().length);

      const micSource = this.audioContext.createMediaStreamSource(this.micStream);
      this.agentWorklet = new AudioWorkletNode(
        this.audioContext,
        'audio-chunk-processor',
      );

      let agentChunkCount = 0;
      this.agentWorklet.port.onmessage = (event) => {
        if (event.data.type === 'audio-chunk' && this.chunkCallback) {
          agentChunkCount++;
          if (agentChunkCount <= 3) {
            console.warn(`[AudioCapture] 🎤 Chunk agente #${agentChunkCount} (${event.data.data.byteLength} bytes)`);
          }
          this.chunkCallback(event.data.data, 'agent');
        }
      };

      micSource.connect(this.agentWorklet);
      // Conectar worklet al sink silencioso para forzar pull del audio graph
      // (gain=0, no produce sonido audible — evita feedback)
      if (this.silentSink) {
        this.agentWorklet.connect(this.silentSink);
      }

      // 4. Capturar audio remoto del widget (si está disponible)
      if (remoteAudioElement) {
        await this.captureRemoteAudio(remoteAudioElement);
      }

      this.isCapturing = true;
      console.warn('[AudioCapture] ✅ Captura iniciada (isCapturing=true)');
    } catch (error) {
      this.cleanup();
      logger.error('[AudioCapture] Error iniciando captura:', error);
      throw error;
    }
  }

  /**
   * Intenta capturar el audio remoto del elemento <audio> del widget.
   */
  private async captureRemoteAudio(
    audioElement: HTMLAudioElement,
  ): Promise<void> {
    if (!this.audioContext) return;

    try {
      // captureStream() puede no estar disponible en todos los navegadores
      const captureStream =
        (audioElement as any).captureStream ??
        (audioElement as any).mozCaptureStream;

      if (!captureStream) {
        logger.warn(
          '[AudioCapture] captureStream() no soportado, solo se transcribirá el agente',
        );
        return;
      }

      const remoteStream: MediaStream = captureStream.call(audioElement);
      this._connectCustomerStream(remoteStream, 'HTMLAudioElement');
    } catch (error) {
      logger.warn(
        '[AudioCapture] No se pudo capturar audio remoto, continuando solo con mic:',
        error,
      );
    }
  }

  /**
   * Agrega el stream de audio remoto después de que la llamada se haya conectado.
   * Útil cuando el <audio> no está disponible al inicio.
   */
  async addRemoteAudio(audioElement: HTMLAudioElement): Promise<void> {
    if (!this.audioContext || !this.isCapturing) {
      logger.warn('[AudioCapture] No está capturando, no se puede agregar audio remoto');
      return;
    }
    await this.captureRemoteAudio(audioElement);
  }

  /**
   * Agrega un MediaStream remoto directamente (sin necesidad de HTMLAudioElement).
   * Útil para WebRTC donde ya tenemos el stream del peer (ej. TelnyxRTC call.remoteStream).
   *
   * IMPORTANTE: El stream se CLONA internamente para evitar conflictos con otros
   * consumidores (ej. el <audio> interno de TelnyxRTC que reproduce el sonido).
   * Sin clonar, Chrome puede entregar frames vacíos al MediaStreamSource.
   */
  async addRemoteStream(stream: MediaStream): Promise<void> {
    if (!this.audioContext || !this.isCapturing) {
      console.warn('[AudioCapture] ⚠️ No está capturando, no se puede agregar stream remoto');
      return;
    }

    console.warn('[AudioCapture] 📞 Agregando stream remoto (MediaStream). Tracks:', stream.getAudioTracks().length, 'active:', stream.active);

    try {
      // CLONAR el stream para evitar conflictos con TelnyxRTC que ya lo consume
      // internamente en un <audio> element. Sin clonar, createMediaStreamSource()
      // puede recibir frames vacíos en Chrome.
      const cloned = stream.clone();
      this.clonedRemoteStream = cloned;
      console.warn('[AudioCapture] 📞 Stream clonado. Original id:', stream.id, '→ Clon id:', cloned.id, 'tracks:', cloned.getAudioTracks().length, 'active:', cloned.active);

      this._connectCustomerStream(cloned, 'MediaStream (clonado)');
    } catch (error) {
      console.error(
        '[AudioCapture] ❌ No se pudo capturar stream remoto, continuando solo con mic:',
        error,
      );
    }
  }

  /**
   * Método interno compartido para conectar un stream de audio del cliente
   * al pipeline de procesamiento (worklet → callback → WebSocket).
   *
   * Conecta el worklet al silentSink (GainNode gain=0 → destination) para
   * garantizar que Chrome invoque process() en el AudioWorkletNode.
   * Sin esta conexión, el audio graph no "tira" del nodo y process() puede
   * no ejecutarse para streams WebRTC remotos.
   */
  private _connectCustomerStream(stream: MediaStream, source: string): void {
    if (!this.audioContext) return;

    const audioTracks = stream.getAudioTracks();
    console.warn(`[AudioCapture] 🔗 _connectCustomerStream(${source}): tracks=${audioTracks.length}, active=${stream.active}, enabled=${audioTracks[0]?.enabled}, readyState=${audioTracks[0]?.readyState}`);

    const remoteSource = this.audioContext.createMediaStreamSource(stream);

    this.customerWorklet = new AudioWorkletNode(
      this.audioContext,
      'audio-chunk-processor',
    );

    let customerChunkCount = 0;
    this.customerWorklet.port.onmessage = (event) => {
      if (event.data.type === 'audio-chunk' && this.chunkCallback) {
        customerChunkCount++;
        if (customerChunkCount <= 5) {
          console.warn(`[AudioCapture] 📞 Chunk cliente #${customerChunkCount} (${event.data.data.byteLength} bytes) [${source}]`);
        }
        this.chunkCallback(event.data.data, 'customer');
      }
    };

    remoteSource.connect(this.customerWorklet);

    // Conectar worklet al sink silencioso para forzar pull del audio graph.
    // Sin esto, Chrome puede no invocar process() para streams WebRTC remotos.
    if (this.silentSink) {
      this.customerWorklet.connect(this.silentSink);
      console.warn(`[AudioCapture] ✅ customerWorklet conectado a silentSink → destination [${source}]`);
    }

    console.warn(`[AudioCapture] ✅ Stream remoto (${source}) capturado exitosamente`);
  }

  /**
   * Registra callback para recibir chunks de audio PCM16.
   */
  onAudioChunk(callback: AudioChunkCallback): void {
    this.chunkCallback = callback;
  }

  /**
   * Detiene la captura y limpia recursos.
   */
  stop(): void {
    this.cleanup();
    this.isCapturing = false;
    logger.info('[AudioCapture] Captura detenida');
  }

  /**
   * Indica si está capturando activamente.
   */
  getIsCapturing(): boolean {
    return this.isCapturing;
  }

  private cleanup(): void {
    // Desconectar worklets
    if (this.agentWorklet) {
      this.agentWorklet.disconnect();
      this.agentWorklet.port.close();
      this.agentWorklet = null;
    }
    if (this.customerWorklet) {
      this.customerWorklet.disconnect();
      this.customerWorklet.port.close();
      this.customerWorklet = null;
    }

    // Desconectar sink silencioso
    if (this.silentSink) {
      this.silentSink.disconnect();
      this.silentSink = null;
    }

    // Detener tracks del micrófono
    if (this.micStream) {
      this.micStream.getTracks().forEach((t) => t.stop());
      this.micStream = null;
    }

    // Detener tracks del stream remoto clonado
    if (this.clonedRemoteStream) {
      this.clonedRemoteStream.getTracks().forEach((t) => t.stop());
      this.clonedRemoteStream = null;
    }

    // Cerrar AudioContext
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }

    this.chunkCallback = null;
  }
}

/**
 * Prepara un chunk de audio con header de canal para enviar por WebSocket.
 * Formato: [1 byte canal] + [PCM16 audio data]
 */
export function packAudioChunk(
  audioData: ArrayBuffer,
  channel: TranscriptionChannel,
): ArrayBuffer {
  const channelByte =
    channel === 'agent' ? AUDIO_CHANNEL_AGENT : AUDIO_CHANNEL_CUSTOMER;
  const packed = new Uint8Array(1 + audioData.byteLength);
  packed[0] = channelByte;
  packed.set(new Uint8Array(audioData), 1);
  return packed.buffer;
}
